import React, { useEffect, useState } from "react";
import { notification } from "antd";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pdf } from '@react-pdf/renderer';

import { changeStatus } from "../../../controllers/V1/userRequestController";
import LoadingEffect from "../../../components/Loading/LoadingEffect";
import RequestDetailsPDF from "./RequestDetailsPDF";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { faArrowLeft, faCheckCircle, faEye, faInfoCircle, faDownload } from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../../config/constants";
import { usePermissions } from "../../../controllers/PermissionContext";
import { getBas264Image } from '../../../controllers/V1/generalDataController';

const VendorListDeletePopUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ errors, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [ADDEDITDATA, setAddEditData] = useState(location?.state ||{});
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const PERMISSION = usePermissions("userRequest");

  // Helper function to detect image format and format base64 data URI
const formatImageDataUri = (base64Image, imageUrl = '') => {
  if (!base64Image || typeof base64Image !== 'string') {
    console.warn('formatImageDataUri: Invalid input', typeof base64Image);
    return null;
  }
  
  // If already formatted as data URI, return as is
  if (base64Image.startsWith('data:')) {
    console.log('formatImageDataUri: Already formatted as data URI');
    return base64Image;
  }
  
  // If it's a URL, return as is (though this shouldn't happen for PDF images)
  if (base64Image.startsWith('http')) {
    console.warn('formatImageDataUri: Received HTTP URL instead of base64');
    return base64Image;
  }
  
  // Remove any whitespace or newlines
  const cleanBase64 = base64Image.replace(/\s/g, '');
  
  // Detect format from URL extension
  let imageType = 'jpeg'; // default
  if (imageUrl) {
    const urlLower = imageUrl.toLowerCase();
    if (urlLower.includes('.png')) {
      imageType = 'png';
    } else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
      imageType = 'jpeg';
    } else if (urlLower.includes('.webp')) {
      imageType = 'webp';
    } else if (urlLower.includes('.gif')) {
      imageType = 'gif';
    }
  }
  
  // Try to detect from base64 data (magic bytes)
  // PNG: starts with iVBORw0KGgo
  // JPEG: starts with /9j/
  // GIF: starts with R0lGOD
  if (cleanBase64.startsWith('iVBORw0KGgo')) {
    imageType = 'png';
  } else if (cleanBase64.startsWith('/9j/')) {
    imageType = 'jpeg';
  } else if (cleanBase64.startsWith('R0lGOD')) {
    imageType = 'gif';
  }
  
  console.log(`formatImageDataUri: Detected type=${imageType}, base64 length=${cleanBase64.length}`);
  return `data:image/${imageType};base64,${cleanBase64}`;
};

  // Helper function to process a single image field
const processImageField = async (imageData, fieldName) => {
  if (!imageData) return null;
  
  try {
    // Extract image URL - handle both string and object formats
    const imageUrl = typeof imageData === 'string' 
      ? imageData 
      : imageData[fieldName] || imageData;
    if (!imageUrl) return null;
    const apiResponse = await getBas264Image({image_url: imageUrl});
    let base64Image = null;
    if (apiResponse && typeof apiResponse === 'object') {
      if (apiResponse.status === false) {
        console.error(`Failed to fetch ${fieldName} image:`, apiResponse.message);
        return null;
      }
      base64Image = apiResponse.data || apiResponse.image || apiResponse.base64 || apiResponse.result || apiResponse;
    } else if (typeof apiResponse === 'string') {
      base64Image = apiResponse;
    }
    
    if (base64Image && typeof base64Image === 'string') {
      const formattedImage = formatImageDataUri(base64Image, imageUrl);
      
      if (formattedImage) {
        return formattedImage;
      } else {
        return null;
      }
    }
    
    return null;
  } catch (error) {
    console.error(`Error processing ${fieldName} image:`, error);
    return null;
  }
};

  /*********************************************************
   *  Download PDF with Images
   *********************************************************/
  const handleDownloadPDF = async () => {
    try {
      setIsDownloadingPDF(true);
      
      // Convert images to base64
      const images = {};
      if (ADDEDITDATA.tradeLicense) {
        images.tradeLicense = await processImageField(ADDEDITDATA.tradeLicense, 'tradeLicense');
      }
      if (ADDEDITDATA.id_proof_front) {
        images.id_proof_front = await processImageField(ADDEDITDATA.id_proof_front, 'id_proof_front');
      }
      if (ADDEDITDATA.id_proof_back) {
        images.id_proof_back = await processImageField(ADDEDITDATA.id_proof_back, 'id_proof_back');
      } 
      if (ADDEDITDATA.signature) {
        images.signature = await processImageField(ADDEDITDATA.signature, 'signature');
      }
      if (ADDEDITDATA.agent_signature) {
        images.agent_signature = await processImageField(ADDEDITDATA.agent_signature, 'agent_signature');
      }
      Object.entries(images).forEach(([key, value]) => {
        console.log(`${key}: type=${typeof value}, isString=${typeof value === 'string'}, starts with data:=${value?.startsWith?.('data:')}`);
      });
      
      // Generate PDF
      const pdfDoc = <RequestDetailsPDF data={ADDEDITDATA} images={images} />;
      const blob = await pdf(pdfDoc).toBlob();
      
      // Download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Request_${ADDEDITDATA?.first_name}_${ADDEDITDATA?.last_name}_${new Date().getTime()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      setIsDownloadingPDF(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notification.error({
        message: "Error",
        description: "Failed to download PDF. Please try again.",
        placement: "topRight",
        duration: 3,
      });
      setIsDownloadingPDF(false);
    }
  };

  const handleChange = async (e) => {
    try {
      const {name, value} = e.target;
      setAddEditData((pre)=>({
        ...pre,
        [name] : value
      }));
    } catch (error) {
      
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError({});
      const form = new FormData(e.target);
      let error = false;
      let errorMessage = "";
      if(!form.get('edit_id')){
        error = true;
        errorMessage = "Request not found! Please try again.";
      }
      if(error === false && !form.get('first_name')){
        error = true;
        errorMessage = "First Name is required.";
      }
      if(error === false && !form.get('last_name')){
        error = true;
        errorMessage = "Last Name is required.";
      }
      if(error === false && !form.get('area')){
        error = true;
        errorMessage = "Area is required.";
      }
      if(error === false && !form.get('store_name')){
        error = true;
        errorMessage = "Store Name is required.";
      }
      if(error === false && !form.get('area')){
        error = true;
        errorMessage = "Area is required.";
      }

      if(error === true){
        notification.open({
          message: "Oops!.",
          description: `${errorMessage}`,
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
          duration: 2,
        });
      } else{
        const option = {
          editId                          : form.get('edit_id'), 
          first_name                      : form.get('first_name'), 
          last_name                       : form.get('last_name'), 
          area                            : form.get('area'), 
          store_name                      : form.get('store_name'), 
          country_code                    : ADDEDITDATA?.country_code, 
          phone                           : ADDEDITDATA?.mobile, 
          email                           : ADDEDITDATA?.email, 
          availableArabianPoints          : parseFloat(form.get('availableArabianPoints')) || 0, 
          contract_given_by               : form.get('referBy') || "", 
          remarks                         : form.get('remarks') || "", 
          status                          : "Approved"
        }
        let res = await changeStatus(option);
        if(res.status === true){
          notification.open({
            message: "Success.",
            description: `Request approved successfully.`,
            placement: "topRight",
            icon: <FontAwesomeIcon style={{ color: "green" }} icon={faCheckCircle} />,
            duration: 2,
          });
          navigate("/users/request-list");
        } else {
          notification.open({
            message: "Oops!.",
            description: res?.message || `No data found.`,
            placement: "topRight",
            icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
            duration: 2,
          });
        }
      }
    } catch (error) {
      console.log("error : ", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  const handleReject = async () => {
    try {
      setIsLoading(true);
      if(!ADDEDITDATA?.remarks || ADDEDITDATA?.remarks === ""){
        notification.open({
          message: "Oops!.",
          description: `Remarks is required`,
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
          duration: 2,
        });
      } else if(!ADDEDITDATA?._id){
        notification.open({
          message: "Oops!.",
          description: `Please try again.`,
          placement: "topRight",
          icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
          duration: 2,
        });
      } else{
        const options = {
          editId : ADDEDITDATA?._id,
          remarks : ADDEDITDATA?.remarks,
          status : "Rejected"
        }
        let res = await changeStatus(options);
        if(res.status === true){
          notification.open({
            message: "Success.",
            description: `Request rejected successfully.`,
            placement: "topRight",
            icon: <FontAwesomeIcon style={{ color: "green" }} icon={faCheckCircle} />,
            duration: 2,
          });
          navigate("/users/request-list");
        } else {
          notification.open({
            message: "Oops!.",
            description: `No data found.`,
            placement: "topRight",
            icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
            duration: 2,
          });
        }
      }
    } catch (error) {
      notification.open({
        message: "Oops!.",
        description: `Please go back and try again.`,
        placement: "topRight",
        icon: <FontAwesomeIcon style={{ color: "red" }} icon={faInfoCircle} />,
        duration: 2,
      });
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      {" "}
      <div className="container-fluid">
        <div className="Inventory_edit_page_main_div">
          <React.Fragment>
            <h5 className="inventory_edit_page_heading"> Request Details </h5>
          </React.Fragment>
          <div style={{display: 'flex', gap: '10px'}}>
            <Link to={"/users/request-list"}>
              <React.Fragment>
                <button type="button" className="btn btn-secondary"> <FontAwesomeIcon icon={faArrowLeft}/> Back </button>
              </React.Fragment>
            </Link>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleDownloadPDF}
              disabled={isDownloadingPDF}
            >
              <FontAwesomeIcon icon={faDownload}/> {isDownloadingPDF ? 'Generating PDF...' : 'Download PDF'}
            </button>
          </div>
        </div>

        <div className="card shadow mb-4 dataBase_table_Add_Data">
        <div
            className="pull-out-add-edit-border-wrap"
            style={{
              border: "12px solid #c8e6c9",
              borderRadius: "12px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
          <div className="card-body">
            <div className="responsive">
              <form className="categories_open_popup_form" onSubmit={handleSubmit} >
                <input type="hidden" name="edit_id" id="edit_id" value={ADDEDITDATA?._id}/>
                <div className="form-group row">
                  
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label for="title" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>First Name :
                    </label>
                    <input name="first_name" id="first_name" placeholder="Enter First Name" value={ADDEDITDATA?.first_name} onChange={handleChange} className="form-control categories_select_tg_enter_product" />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label for="title" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Last Name :
                    </label>
                    <input name="last_name" id="last_name" placeholder="Enter Last Name" value={ADDEDITDATA?.last_name} onChange={handleChange} className="form-control categories_select_tg_enter_product" />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label for="users_type" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Users Type
                    </label> 
                    <input type="text" name="users_type" id="users_type" value={"Retailer"} onChange={handleChange} disabled className="form-control"/>
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label for="area" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Area
                    </label> 
                    <input type="text" name="area" id="area" value={ADDEDITDATA?.area} onChange={handleChange} className="form-control " placeholder="Enter Area"/>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <label for="store_name" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Store Name
                    </label> 
                    <input type="text" name="store_name" id="store_name" value={ADDEDITDATA?.store_name} onChange={handleChange} className="form-control " placeholder="Enter Store Name"/>
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label for="email" className="all_lable_for_vendor_dashbaord" >
                      Email
                    </label>
                    <input type="text" name="email" id="email" value={ADDEDITDATA?.email} className="form-control categories_select_tg_enter_product" placeholder="Enter Email Address"/>
                  </div>

                  <div className="col-lg-2 col-md-2 col-sm-12 col-xs-12">
                    <label for="country_code" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Country Code
                    </label>
                    <select name="country_code" id="country_code" disabled className="form-control categories_select_tg_enter_product">
                      <option value={"+971"} selected>+971 (UAE)</option>
                    </select> 
                  </div>

                  <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <label for="country_code" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Phone
                    </label> 
                    <input type="number" name="mobile" id="mobile" value={ADDEDITDATA?.mobile} disabled className="form-control " placeholder="Enter Phone Number"/>
                  </div>

                  <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <label for="bind_user_type" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Bind User Type
                    </label> 
                    <input type="text" name="bind_user_type" id="bind_user_type" value={ADDEDITDATA?.bind_person_id?.users_type} disabled className="form-control " placeholder="Enter User Type"/>
                  </div>

                  <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <label for="bind_user_name" className="all_lable_for_vendor_dashbaord" >
                      <span style={{color:"red"}}>*</span>Bind User Name
                    </label> 
                    <input type="text" name="bind_user_name" id="bind_user_name" value={ADDEDITDATA?.bind_person_id?.users_name} disabled className="form-control " placeholder="Enter Bind User Name"/>
                  </div>

                  <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <label for="referBy" className="all_lable_for_vendor_dashbaord" >Contract given by :</label> 
                    <input type="text" name="referBy" id="referBy" value={ADDEDITDATA?.referBy} onChange={handleChange} className="form-control " placeholder="Enter Contract Given By"/>
                  </div>

                  <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12">
                    <label for="redeeming_commission_percentage" className="all_lable_for_vendor_dashbaord" >
                      Trade License No :
                    </label> 
                    <input type="text" name="trade_license_no" id="trade_license_no" value={ADDEDITDATA?.trade_license_no} disabled onChange={handleChange} className="form-control " placeholder="Enter Trade License No"/>
                  </div>
                  {(ADDEDITDATA?.status === "Approved" || ADDEDITDATA?.status === "Completed") && ADDEDITDATA?.pos_number && (
                    <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12">
                      <label for="redeeming_commission_percentage" className="all_lable_for_vendor_dashbaord" >
                        POS Number :
                      </label> 
                      <input type="text" name="pos_number" id="pos_number" value={ADDEDITDATA?.pos_number} disabled onChange={handleChange} className="form-control " placeholder="Enter POS Number"/>
                    </div>
                  )}


                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <label className="all_lable_for_vendor_dashbaord" >
                     Attached Documents :
                    </label> 
                  </div>

                  {(ADDEDITDATA?.tradeLicense || ADDEDITDATA?.id_proof_front || ADDEDITDATA?.id_proof_back || ADDEDITDATA?.signature) && (
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                      {ADDEDITDATA?.tradeLicense && (
                        <>
                          <Link to={`${API_BASE_URL}${ADDEDITDATA?.tradeLicense}`} target="_blank"> <FontAwesomeIcon icon={faEye} /> Trade License</Link> <br/>
                        </>
                      )}
                      {ADDEDITDATA?.id_proof_front && (
                        <>
                          <Link to={`${API_BASE_URL}${ADDEDITDATA?.id_proof_front}`} target="_blank"> <FontAwesomeIcon icon={faEye} /> ID Proof Front</Link><br/>
                        </>
                      )}
                      {ADDEDITDATA?.id_proof_back && (
                        <>
                          <Link to={`${API_BASE_URL}${ADDEDITDATA?.id_proof_back}`} target="_blank"> <FontAwesomeIcon icon={faEye} /> ID Proof Back</Link><br/>
                        </>
                      )}
                      {ADDEDITDATA?.signature && (
                        <>
                          <Link to={`${API_BASE_URL}${ADDEDITDATA?.signature}`} target="_blank"> <FontAwesomeIcon icon={faEye} /> Signature</Link><br/>
                        </>
                      )}
                      {ADDEDITDATA?.agent_signature && (
                        <>
                          <Link to={`${API_BASE_URL}${ADDEDITDATA?.agent_signature}`} target="_blank"> <FontAwesomeIcon icon={faEye} /> Field Officer Signature</Link><br/>
                        </>
                      )}
                      {ADDEDITDATA?.pos_image && (
                        <>
                          <Link to={`${API_BASE_URL}${ADDEDITDATA?.pos_image}`} target="_blank"> <FontAwesomeIcon icon={faEye} /> POS IMage</Link><br/>
                        </>
                      )}
                    </div>
                  )}

                  {ADDEDITDATA?.bdm_status === "Approved" && (
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                      <label for="admin_remarks" className="all_lable_for_vendor_dashbaord" >BDM Remarks </label> 
                      <textarea disabled className="form-control">{ADDEDITDATA?.bdm_remarks || "N/A"}</textarea>
                    </div>
                  )}

                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <label for="remarks" className="all_lable_for_vendor_dashbaord" >Remarks </label> 
                    <textarea name="remarks" id="remarks" className="form-control" disabled={ADDEDITDATA?.status !== "Pending"} onChange={handleChange}>{ADDEDITDATA?.remarks}</textarea>
                  </div>

                  {ADDEDITDATA?.status === "Pending" ? (
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                      {(
                        (
                          PERMISSION?.bdm_chnage_ststus === 'Y' &&
                          ADDEDITDATA?.bdm_status === "Pending" &&
                          ADDEDITDATA?.deployment_status === "Pending" &&
                          ADDEDITDATA?.status === "Pending"

                        ) || (
                          PERMISSION?.deployment_chnage_ststus === 'Y' &&
                          ADDEDITDATA?.bdm_status === "Approved" &&
                          ADDEDITDATA?.deployment_status === "Pending" &&
                          ADDEDITDATA?.status === "Pending"
                        ) || PERMISSION?.fullAccess === 'Y') && (
                          <div className="inventory_open_popup_action_btn">
                            {(PERMISSION?.complete === 'Y' || PERMISSION?.fullAccess === 'Y') && (
                              <button type="submit" className="btn btn-success categories_cancel_button" style={{width : "25%"}} > Approved </button>
                            )}
                            {(PERMISSION?.reject === 'Y' || PERMISSION?.fullAccess === 'Y') && (
                              <button type="button" className="btn btn-danger" style={{width : "25%"}} onClick={handleReject} > Reject </button>
                            )}
                          </div>
                        )}
                    </div>
                  ):(
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                      <div className="inventory_open_popup_action_btn">
                        <span className="btn btn-success" style={{"width":"100%"}} > {`Request is ${ADDEDITDATA?.status} by ${ADDEDITDATA?.updated_by?.account_id || "Admin"}`} </span>
                      </div>
                    </div>
                  )}

                </div>
              </form>
            </div>
          </div>
          </div>
        </div>
      </div>
      <LoadingEffect isLoading={isLoading} />
    </div>
    
  );
};

export default VendorListDeletePopUp;
